import React, { memo, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Filter, Check, X, Edit, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI, segmentsAPI } from '../../api';

const segmentColorOptions = [
  { value: 'bg-blue-100 text-blue-700', label: 'Blue' },
  { value: 'bg-emerald-100 text-emerald-700', label: 'Green' },
  { value: 'bg-purple-100 text-purple-700', label: 'Purple' },
  { value: 'bg-amber-100 text-amber-700', label: 'Amber' },
  { value: 'bg-rose-100 text-rose-700', label: 'Rose' },
  { value: 'bg-cyan-100 text-cyan-700', label: 'Cyan' },
  { value: 'bg-slate-100 text-slate-700', label: 'Slate' },
];

const SegmentsDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  initialSegments = [],
  onSuccess 
}) => {
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [customerSegments, setCustomerSegments] = useState([]);
  const [editingSegment, setEditingSegment] = useState(null);
  const [newSegmentForm, setNewSegmentForm] = useState({ label: '', color: 'bg-blue-100 text-blue-700' });
  const [isCreatingSegment, setIsCreatingSegment] = useState(false);

  const fetchSegments = useCallback(async () => {
    try {
      const res = await segmentsAPI.getAll();
      setCustomerSegments(res.data.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments');
    }
  }, []);

  // Initialize when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSegments(initialSegments || []);
      fetchSegments();
    }
  }, [open, initialSegments, fetchSegments]);

  const handleClose = useCallback(() => {
    setEditingSegment(null);
    setIsCreatingSegment(false);
    setNewSegmentForm({ label: '', color: 'bg-blue-100 text-blue-700' });
    onOpenChange(false);
  }, [onOpenChange]);

  const toggleSegment = useCallback((segmentValue) => {
    setSelectedSegments(prev => 
      prev.includes(segmentValue) 
        ? prev.filter(s => s !== segmentValue)
        : [...prev, segmentValue]
    );
  }, []);

  const handleSaveSegments = useCallback(async () => {
    try {
      await contactsAPI.update(contactId, { segments: selectedSegments });
      toast.success('Segments updated');
      handleClose();
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to update segments');
    }
  }, [contactId, selectedSegments, handleClose, onSuccess]);

  const handleCreateSegment = useCallback(async () => {
    if (!newSegmentForm.label.trim()) {
      toast.error('Segment name is required');
      return;
    }
    try {
      const segmentValue = newSegmentForm.label.toLowerCase().replace(/\s+/g, '_');
      await segmentsAPI.create({
        value: segmentValue,
        label: newSegmentForm.label,
        color: newSegmentForm.color
      });
      toast.success('Segment created');
      setNewSegmentForm({ label: '', color: 'bg-blue-100 text-blue-700' });
      setIsCreatingSegment(false);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to create segment');
    }
  }, [newSegmentForm]);

  const handleUpdateSegment = useCallback(async (segmentId) => {
    if (!editingSegment?.label.trim()) {
      toast.error('Segment name is required');
      return;
    }
    try {
      await segmentsAPI.update(segmentId, {
        label: editingSegment.label,
        color: editingSegment.color
      });
      toast.success('Segment updated');
      setEditingSegment(null);
      fetchSegments();
    } catch (error) {
      toast.error('Failed to update segment');
    }
  }, [editingSegment]);

  const handleDeleteSegment = useCallback(async (segmentId) => {
    if (!confirm('Are you sure you want to delete this segment? Contacts with this segment will have it removed.')) {
      return;
    }
    try {
      await segmentsAPI.delete(segmentId);
      toast.success('Segment deleted');
      setSelectedSegments(prev => prev.filter(s => s !== segmentId));
      fetchSegments();
    } catch (error) {
      toast.error('Failed to delete segment');
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Manage Customer Segments
          </DialogTitle>
          <DialogDescription>
            Assign segments to categorize this customer. Click edit to modify segment details.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4 max-h-80 overflow-y-auto">
          {customerSegments.map(segment => (
            <div 
              key={segment.value}
              className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                selectedSegments.includes(segment.value) 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {editingSegment?.value === segment.value ? (
                // Edit mode
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={editingSegment.label}
                    onChange={(e) => setEditingSegment(prev => ({ ...prev, label: e.target.value }))}
                    className="h-8 w-40"
                    data-testid={`segment-edit-input-${segment.value}`}
                  />
                  <Select 
                    value={editingSegment.color} 
                    onValueChange={(v) => setEditingSegment(prev => ({ ...prev, color: v }))}
                  >
                    <SelectTrigger className="h-8 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {segmentColorOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded-full ${opt.value.split(' ')[0]}`} />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => handleUpdateSegment(segment.id || segment.value)}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingSegment(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                // View mode
                <>
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => toggleSegment(segment.value)}
                    data-testid={`segment-${segment.value}`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${segment.color}`}>
                      {segment.label[0]}
                    </div>
                    <span className="font-medium">{segment.label}</span>
                    {selectedSegments.includes(segment.value) && (
                      <Check className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSegment({ ...segment });
                      }}
                      data-testid={`segment-edit-${segment.value}`}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-600 hover:text-red-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSegment(segment.id || segment.value);
                      }}
                      data-testid={`segment-delete-${segment.value}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
          
          {/* Create New Segment */}
          {isCreatingSegment ? (
            <div className="flex items-center gap-2 p-3 border border-dashed rounded-lg border-blue-300 bg-blue-50/50">
              <Input
                placeholder="Segment name..."
                value={newSegmentForm.label}
                onChange={(e) => setNewSegmentForm(prev => ({ ...prev, label: e.target.value }))}
                className="h-8 flex-1"
                data-testid="new-segment-input"
              />
              <Select 
                value={newSegmentForm.color} 
                onValueChange={(v) => setNewSegmentForm(prev => ({ ...prev, color: v }))}
              >
                <SelectTrigger className="h-8 w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {segmentColorOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <div className={`h-3 w-3 rounded-full ${opt.value.split(' ')[0]}`} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleCreateSegment} data-testid="create-segment-btn">
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsCreatingSegment(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              className="w-full border-dashed"
              onClick={() => setIsCreatingSegment(true)}
              data-testid="add-segment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Segment
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Selected:</span>
          {selectedSegments.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedSegments.map(s => {
                const seg = customerSegments.find(cs => cs.value === s);
                return (
                  <Badge key={s} className={seg?.color || 'bg-slate-100'}>
                    {seg?.label || s}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <span className="text-slate-400">None</span>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveSegments} data-testid="save-segments-btn">
            Save Segments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

SegmentsDialog.displayName = 'SegmentsDialog';

export default SegmentsDialog;
